import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ShieldCheck } from 'lucide-react';
import { useLanguage } from '@core/i18n';
import { Button } from '@shared/components/ui/button';
import { verifyAttrs } from '@shared/verify/core/contract';
import { BookingBreadcrumbs } from '@features/booking/components/BookingBreadcrumbs';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { PUBLIC_SEARCH_MODE } from '@shared/types/restaurant';
import { useAuth } from '@core/auth/AuthProvider';
import { useRefreshAuthProfile } from '@core/auth';
import { useAnalytics, ANALYTICS_EVENTS } from '@core/analytics';
import { resolvePairingName } from '@features/booking/utils/pairings';
import { localizeAreaById } from '@features/booking/utils/areaI18n';
import { EarlyAccessBanner } from '@features/booking/components/EarlyAccessBanner';
import { GuestDetailsCard } from '@features/booking/components/GuestDetailsCard';
import { useReservationCommit } from '@features/booking/hooks/useReservationCommit';
import { getDisplayCurrency } from '@features/booking/services/commit';
import { ThreeDSChallengeOverlay } from '@features/booking/components/ThreeDSChallengeOverlay';
import { commitCtaKey } from '@features/booking/utils/commitMomentCopy';

const ConfirmationPage = () => {
  const { t } = useTranslation('booking');
  const { t: tErrors } = useTranslation('errors');
  const { dateLocale, currentLanguage } = useLanguage();
  const booking = useBooking();
  const restaurant = useRestaurant();
  const { authState } = useAuth();
  const { trackEvent } = useAnalytics();
  useRefreshAuthProfile();

  const { commit, isSubmitting, error, requireReload, isHandling3ds } = useReservationCommit();

  useEffect(() => {
    trackEvent({ name: ANALYTICS_EVENTS.BOOKING_CONFIRM });
  }, [trackEvent]);

  // reservationId survives in sessionStorage until BOOKING_FINAL runs
  // resetBooking. A back-navigation from /booking-final lands here with an id
  // still set; re-firing the grab endpoint would be blocked by the backend
  // with 412 concurrent_post, so the CTA switches to continue-only instead.
  const hasExistingReservation = !!booking.reservationId;


  const displayCurrency = getDisplayCurrency(restaurant);

  const userDisplayName = !booking.isAgentReservation ? (authState.name ?? '').trim() : '';


  const formattedDate = booking.selectedDate
    ? format(new Date(booking.selectedDate + 'T12:00:00'), 'EEEE, MMMM d, yyyy', { locale: dateLocale })
    : '';

  const isWaitingList = booking.flowType === 'waiting_list';

  // Decision-affecting state for the verification framework. The branch
  // identifies which top-level rendering path is active; the rest exposes
  // the predicates that gate each in-flow region (guarantee notice, error,
  // submit button label/disabled state).
  const verifyBranch: 'already-created' | 'waiting-list' | 'early-access' | 'standard' =
    hasExistingReservation
      ? 'already-created'
      : isWaitingList
        ? 'waiting-list'
        : booking.isEarlyAccessFlow
          ? 'early-access'
          : 'standard';
  const paymentRequired =
    booking.guaranteeType !== 'traditional' && booking.feeDeposit > 0;

  // Resolved experience name — used both in the SummaryRow below and exposed
  // via verifyAttrs so the verify matrix can assert the menus lookup path.
  const verifyExperienceName: string | null = booking.selectedExperience
    ? (restaurant.menus?.[currentLanguage]?.find(
        (m: { id: number; name: string }) => String(m.id) === booking.selectedExperience!.id
      )?.name ?? booking.selectedExperience.title)
    : null;

  // Resolved area name — same pattern as the experience above: used in the
  // SummaryRow and exposed via verifyAttrs so the matrix can assert the
  // publicAreas lookup. selectedArea holds the CANONICAL name (matching logic
  // reads it); localization happens only for display.
  const verifyAreaName: string | null = booking.selectedArea
    ? localizeAreaById(booking.selectedArea, restaurant.publicAreas, currentLanguage).name
    : null;



  return (
    <BookingPageLayout
      title={hasExistingReservation
        ? t('CONFIRMATION_ALREADY_CREATED_TITLE')
        : isWaitingList
          ? t('WAITING_LIST_CONFIRM_TITLE')
          : booking.isEarlyAccessFlow
            ? t('EARLY_ACCESS_BANNER_TITLE')
            : t('CONFIRMATION_TITLE')}
      subtitle={hasExistingReservation
        ? t('CONFIRMATION_ALREADY_CREATED_SUBTITLE')
        : isWaitingList
          ? t('WAITING_LIST_CONFIRM_SUBTITLE')
          : booking.isEarlyAccessFlow
            ? t('EARLY_ACCESS_IS_NOT_A_RESERVATION')
            : t('CONFIRMATION_SUBTITLE')}
      subtitleClassName="text-foreground/50 text-sm mb-10 text-center sm:text-left"
      breadcrumbs={<BookingBreadcrumbs />}
    >
      <div
        className="w-full sm:px-6 space-y-6"
        {...verifyAttrs("ConfirmationPage", {
          branch: verifyBranch,
          paymentRequired,
          hasReservationId: hasExistingReservation,
          isSubmitting,
          hasError: !!error,
          isWaitingList,
          isEarlyAccess: booking.isEarlyAccessFlow,
          feeDeposit: booking.feeDeposit,
          guaranteeType: booking.guaranteeType,
          isAgent: booking.isAgentReservation,
          diners: booking.diners,
          experienceName: verifyExperienceName ?? "",
          areaName: verifyAreaName ?? "",
          expectsSuccess: hasExistingReservation,
          // T30 conformance: expose the host-step + resolved CTA key so the
          // matrix can pin the contract. ConfirmationPage maps to
          // hostStep="confirm" (per commitCtaKey's policy: confirm and
          // personalize share the simple "Reservar" CTA — the fee has
          // already been shown earlier in the breakdown).
          hostStep: "confirm",
          // Mirror the button-label branch order: isSubmitting takes
          // precedence over the WL/EA/already-created branches so the
          // verify contract reflects what the user actually sees.
          ctaKey: requireReload
            ? "PAYMENT_3DS_RELOAD_TO_RETRY"
            : isSubmitting
              ? "COMMIT_CTA_LOADING"
              : hasExistingReservation
                ? "CONFIRMATION_CONTINUE_TO_DETAILS"
                : isWaitingList
                  ? "CALENDAR_DIALOG_WAITING_LIST_BTN_2"
                  : booking.isEarlyAccessFlow
                    ? "EARLY_ACCESS_REQUEST_EARLY_ACCESS"
                    : commitCtaKey({
                        hostStep: "confirm",
                        hasFee: paymentRequired,
                        isSubmitting,
                        hasError: !!error,
                        // Always false here at runtime — the WL short-circuit
                        // above returns CALENDAR_DIALOG_WAITING_LIST_BTN_2.
                        // Pass the real value so the helper stays robust if
                        // that branching logic ever changes.
                        isWaitingList,
                      }),
          requireReload,
        })}
      >
        {/* Booking Summary */}
        <div className="bg-foreground/5 rounded-xl p-5 space-y-4">
          <h3 className="font-display text-lg font-semibold text-foreground">
            {isWaitingList
              ? t('WAITING_LIST_CONFIRM_TITLE')
              : booking.isEarlyAccessFlow
                ? t('EARLY_ACCESS_REQUEST_SUMMARY')
                : t('NEW_MODAL_BOOKING_DETAILS')}
          </h3>

          {userDisplayName && (
            <SummaryRow
              label={t('profile:MY_ACCOUNT_LABEL_NAME')}
              value={userDisplayName}
            />
          )}
          <SummaryRow
            label={t('CONFIRMATION_GUESTS')}
            value={t('PARTYSIZE_LABEL', { count: booking.diners })}
          />
          {isWaitingList ? (
            <div className="space-y-1">
              <span className="text-sm text-foreground/50">{t('BOOKING_FINAL_REQUESTED_DATES')}</span>
              <div className="flex flex-wrap gap-1 justify-end">
                {booking.wlDates.map((d) => (
                  <span key={d} className="text-sm font-medium text-foreground bg-foreground/10 px-2 py-0.5 rounded">
                    {format(new Date(d + 'T12:00:00'), 'MMM d', { locale: dateLocale })}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <SummaryRow
              label={t('CONFIRMATION_DATE')}
              value={formattedDate}
            />
          )}
          <SummaryRow
            label={t('CONFIRMATION_TIME')}
            value={isWaitingList
              ? (booking.wlPreferredTime || t('NEW_MODAL_FLEXIBLE'))
              : (booking.selectedTime || '')}
          />
          {(booking.service || isWaitingList) && (
            <SummaryRow
              label={t('CONFIRMATION_SERVICE')}
              value={isWaitingList
                ? (booking.wlServiceType
                    ? t(`FILTER_STRING_${booking.wlServiceType.toUpperCase()}`)
                    : t('NEW_MODAL_ANY_SERVICE'))
                : t(`FILTER_STRING_${booking.service!.toUpperCase()}`)}
            />
          )}
          {booking.selectedArea && !isWaitingList && restaurant.publicSearchMode === PUBLIC_SEARCH_MODE.AREA && (
            <SummaryRow
              label={t('CONFIRMATION_AREA')}
              value={toTitleCase(verifyAreaName ?? booking.selectedArea.name)}
            />
          )}
          {booking.selectedExperience && verifyExperienceName !== null && (() => {
            // verifyExperienceName is pre-computed above (menus lookup with
            // title fallback) — reuse it here to keep the logic in one place.
            const displayName = restaurant.publicSearchMode === PUBLIC_SEARCH_MODE.AREA
              ? toTitleCase(verifyExperienceName)
              : verifyExperienceName;
            return (
              <SummaryRow
                label={t('CONFIRMATION_EXPERIENCE')}
                value={displayName}
              />
            );
          })()}
        </div>

        {/* Personalization Summary */}
        {(booking.allergies || booking.veggie || booking.kids || booking.pets || booking.accessibility) && (
          <div className="bg-foreground/5 rounded-xl p-5 space-y-4">
            <h3 className="font-display text-lg font-semibold text-foreground">
              {t('CONFIRMATION_PREFERENCES')}
            </h3>

            {booking.allergies && (
              <SummaryRow
                label={t('STRING_MENU_ALLERGIES_TITLE')}
                value={booking.allergies}
              />
            )}
            {booking.veggie && (
              <SummaryRow
                label={t('NEW_MODAL_GUESTS_VEGETARIAN')}
                value={String(booking.howManyVeggie)}
              />
            )}
            {booking.kids && (
              <SummaryRow
                label={t('NEW_MODAL_GUESTS_CHILDREN')}
                value={String(booking.howManyKids)}
              />
            )}
            {booking.pets && (
              <SummaryRow
                label={t('NEW_MODAL_PET')}
                value={t('STRING_YES')}
              />
            )}
            {booking.accessibility && (
              <SummaryRow
                label={t('NEW_MODAL_ACCESSIBILITY_TABLE')}
                value={t('STRING_YES')}
              />
            )}
          </div>
        )}

        {/* Pairings Summary */}
        {booking.selectedPairings.length > 0 && (
          <div className="bg-foreground/5 rounded-xl p-5 space-y-4">
            <h3 className="font-display text-lg font-semibold text-foreground">
              {t('CONFIRMATION_PAIRINGS')}
            </h3>
            {booking.selectedPairings.map((p) => {
              const resolvedName = resolvePairingName(
                p.id,
                p.name,
                restaurant.beveragePairings,
                currentLanguage,
              );
              return (
                <SummaryRow
                  key={p.id}
                  label={`${resolvedName} (x${p.quantity})`}
                  value={`${displayCurrency} ${(p.price * p.quantity).toLocaleString()}`}
                />
              );
            })}
          </div>
        )}

        {/* Agent: Guest Info Summary */}
        {booking.isAgentReservation && booking.guestName && (
          <GuestDetailsCard
            name={booking.guestName}
            email={booking.guestEmail}
            phone={booking.guestPhone}
            showAgentNote={booking.requestGuarantee}
          />
        )}

        {/* Guarantee Info */}
        {!hasExistingReservation && booking.guaranteeType !== 'traditional' && booking.feeDeposit > 0 && (
          <div
            className="bg-foreground/5 rounded-xl p-5 space-y-2"
            {...verifyAttrs("ConfirmationGuaranteeNotice", {
              guaranteeType: booking.guaranteeType,
              feeDeposit: booking.feeDeposit,
              currency: displayCurrency,
            })}
          >
            <div className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-brand shrink-0" aria-hidden="true" />
              <h3 className="font-display text-lg font-semibold text-foreground">
                {t('CONFIRMATION_GUARANTEE')}
              </h3>
            </div>
            <p className="text-sm text-foreground/70">
              {booking.guaranteeType === 'deposit'
                ? t('CONFIRMATION_DEPOSIT_GUARANTEE', { amount: booking.feeDeposit, currency: displayCurrency })
                : t('CONFIRMATION_NO_SHOW_GUARANTEE', { amount: booking.feeDeposit, currency: displayCurrency })}
            </p>
          </div>
        )}

        {/* Existing reservation notice */}
        {hasExistingReservation && (
          <div
            data-testid="confirmation-already-created-notice"
            className="bg-brand-muted/40 text-foreground rounded-xl p-4 text-sm"
          >
            {t('CONFIRMATION_ALREADY_CREATED_NOTICE')}
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            data-testid="confirmation-error"
            className="bg-red-50 text-red-700 rounded-xl p-4 text-sm"
            {...verifyAttrs("ConfirmationError", { hasError: true })}
          >
            {error}
          </div>
        )}
      </div>

      <div className="w-full sm:px-6 mt-8">
        <Button
          data-testid="confirm-booking-btn"
          {...verifyAttrs("ConfirmationSubmit", { action: "confirm-booking" })}
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
          onClick={commit}
          disabled={isSubmitting || requireReload}
        >
          {requireReload
            ? tErrors('PAYMENT_3DS_RELOAD_TO_RETRY')
            : // In-flight loading copy precedes branch-specific labels so
              // WL / early-access / already-created flows still get the
              // "Creando reserva…" feedback on slow mobile during commit
              // (the disabled button alone wasn't enough signal). Pinned
              // here above the WL/EA branches that don't go through
              // commitCtaKey — without this they'd hold the steady-state
              // label while the request was in-flight.
              isSubmitting
              ? t('COMMIT_CTA_LOADING')
              : hasExistingReservation
                ? t('CONFIRMATION_CONTINUE_TO_DETAILS')
                : isWaitingList
                  ? t('CALENDAR_DIALOG_WAITING_LIST_BTN_2')
                  : booking.isEarlyAccessFlow
                    ? t('EARLY_ACCESS_REQUEST_EARLY_ACCESS')
                    : // T30 commit-moment copy: state-aware CTA key for
                      // the standard flow. `isSubmitting` is handled
                      // above so this branch covers retry-after-error
                      // (COMMIT_CTA_RETRY) and the steady-state Confirm
                      // CTA (COMMIT_CTA_PERSONALIZE — the spec routes
                      // confirm + personalize to the same "Reservar"
                      // copy).
                      t(commitCtaKey({
                        hostStep: 'confirm',
                        hasFee: paymentRequired,
                        isSubmitting,
                        hasError: !!error,
                        isWaitingList,
                      }))}
        </Button>
      </div>

      {/* T30 D2: full-screen overlay while bank verifies the 3DS challenge */}
      <ThreeDSChallengeOverlay open={isHandling3ds} />
    </BookingPageLayout>
  );
};

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-4 items-baseline">
      <span className="text-sm text-foreground/50">{label}</span>
      <span className="text-sm font-medium text-foreground text-right break-words [overflow-wrap:anywhere] tabular-nums">
        {value}
      </span>
    </div>
  );
}

function toTitleCase(text: string): string {
  return text
    .toLocaleLowerCase()
    .replace(/(^|\s|-|\()(\p{L})/gu, (_, sep: string, ch: string) => sep + ch.toLocaleUpperCase());
}

export default ConfirmationPage;
